import React, { useState, useEffect } from "react";
import "../../styles/UserForm.css";
import axiosClient from "../../api/axiosClient";

function TenantEdit({ tenantData, onClose, onSuccess, onOpenPermissions }) {
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const [form, setForm] = useState({
    name: "",
    code: "",
    domain: "",
    contact_email: "",
    logo_url: "",
    theme_color: "",
    status: "Active",
  });

  useEffect(() => {
    if (tenantData) {
      setForm({
        name: tenantData.name || "",
        code: tenantData.code || "",
        domain: tenantData.domain || "",
        contact_email: tenantData.contact_email || "",
        logo_url: tenantData.logo_url || "",
        theme_color: tenantData.theme_color || "#007bff",
        status: tenantData.status || "Active",
      });
    }
  }, [tenantData]);

  const updateForm = (key, value) => {
    setError("");
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const validate = () => {
    if (!form.name.trim()) {
      setError("Tenant name is required");
      return false;
    }
    return true;
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    try {
      setSaving(true);
      const res = await axiosClient.put(`/tenant/api/${tenantData.id}`, {
        name: form.name.trim(),
        domain: form.domain.trim() || null,
        contact_email: form.contact_email.trim() || null,
        logo_url: form.logo_url.trim() || null,
        theme_color: form.theme_color,
        status: form.status,
      });

      if (res?.data?.success) {
        onSuccess?.();
        onClose?.();
      } else {
        setError(res?.data?.message || "Failed to update tenant");
      }
    } catch (err) {
      setError("Failed to update tenant");
    } finally {
      setSaving(false);
    }
  };

  if (!tenantData) return null;

  return (
    <div className="uf-modal" onClick={onClose}>
      <div className="uf-card" onClick={(e) => e.stopPropagation()}>
        {/* ✅ Gradient Header */}
        <div className="uf-header">
          <h5 className="text-white">Edit Tenant</h5>
          <button type="button" onClick={onClose}>✕</button>
        </div>

        {/* ✅ Body */}
        <form onSubmit={handleUpdate}>
          <div className="uf-body">
            {error && <div className="alert alert-danger py-2">{error}</div>}

            <div className="uf-grid">
              {/* Name */}
              <div>
                <label className="form-label">Name *</label>
                <input
                  className="form-control"
                  value={form.name}
                  onChange={(e) => updateForm("name", e.target.value)}
                />
              </div>

              {/* Code - read only */}
              <div>
                <label className="form-label">Code (not editable)</label>
                <input
                  className="form-control"
                  value={form.code}
                  disabled
                  readOnly
                />
              </div>

              {/* Domain */}
              <div>
                <label className="form-label">Domain</label>
                <input
                  className="form-control"
                  value={form.domain}
                  onChange={(e) => updateForm("domain", e.target.value)}
                />
              </div>

              {/* Contact Email */}
              <div>
                <label className="form-label">Contact Email</label>
                <input
                  type="email"
                  className="form-control"
                  value={form.contact_email}
                  onChange={(e) =>
                    updateForm("contact_email", e.target.value)
                  }
                />
              </div>

              {/* Theme Color */}
              <div>
                <label className="form-label">Theme Color</label>
                <input
                  type="color"
                  className="form-control form-control-color"
                  value={form.theme_color}
                  onChange={(e) =>
                    updateForm("theme_color", e.target.value)
                  }
                />
              </div>

              {/* Logo URL */}
              <div>
                <label className="form-label">Logo URL</label>
                <input
                  className="form-control"
                  value={form.logo_url}
                  onChange={(e) =>
                    updateForm("logo_url", e.target.value)
                  }
                />
              </div>

              {/* ✅ Status */}
              <div className="form-check form-switch mt-4">
                <input
                  type="checkbox"
                  className="form-check-input"
                  checked={form.status === "Active"}
                  onChange={(e) =>
                    updateForm("status", e.target.checked ? "Active" : "Suspended")
                  }
                />
                <label className="form-check-label">
                  Active (toggle to Suspend)
                </label>
              </div>
            </div>
          </div>

          {/* ✅ Footer */}
          <div className="uf-actions d-flex justify-content-between align-items-center flex-wrap gap-2">
            {/* Gradient Button — Consistent Look */}
            <button
              type="button"
              className="btn btn-gradient-primary"
              onClick={() => onOpenPermissions?.(tenantData.id)}
            >
              ⚙️ Manage Role Permissions
            </button>

            <div className="d-flex gap-2">
              <button
                type="button"
                className="btn btn-cancel"
                onClick={onClose}
              >
                Cancel
              </button>
              <button
                className="btn btn-primary"
                type="submit"
                disabled={saving}
              >
                {saving ? "Updating…" : "Update Tenant"}
              </button>
            </div>
          </div>
        </form>
      </div>

      {/* ✅ Unified Gradient Style */}
      <style>{`
        .btn-gradient-primary {
          background: linear-gradient(90deg, #007bff, #6f42c1);
          color: #fff !important;
          font-weight: 600;
          border: none;
          border-radius: 6px;
          padding: 6px 14px;
          font-size: 14px;
          height: 38px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          transition: all 0.2s ease-in-out;
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
        }
        .btn-gradient-primary:hover {
          transform: translateY(-1px);
          box-shadow: 0 3px 8px rgba(0, 0, 0, 0.25);
        }
        .btn-gradient-primary:active {
          transform: translateY(0);
          box-shadow: 0 1px 4px rgba(0, 0, 0, 0.2);
        }
      `}</style>
    </div>
  );
}

export default TenantEdit;
